import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Eye,
  Mail,
  Building2,
  Phone,
  Key,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MoreHorizontal,
  Send
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminCustomers() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  
  // Create customer dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    password: "",
    industry: "custom",
    phone_number: "",
    plan_id: "starter",
    safe_mode_enabled: true,
    notes: ""
  });
  
  // Password reset dialog
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  
  // View customer dialog
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerUsage, setCustomerUsage] = useState(null);

  useEffect(() => {
    loadData();
  }, [statusFilter, planFilter]);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      // Load customers
      let url = `${API_URL}/api/admin/settings/customers?limit=100`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      if (planFilter !== "all") {
        url += `&plan=${planFilter}`;
      }
      
      const [customersRes, plansRes] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/settings/plans`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (customersRes.ok) {
        const data = await customersRes.json();
        setCustomers(data.customers || []);
      }
      
      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    
    setCreating(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCustomer)
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Customer created: ${result.email}`);
        setIsCreateDialogOpen(false);
        setNewCustomer({
          name: "",
          email: "",
          password: "",
          industry: "custom",
          phone_number: "",
          plan_id: "starter",
          safe_mode_enabled: true,
          notes: ""
        });
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create customer");
      }
    } catch (error) {
      toast.error("Failed to create customer");
    } finally {
      setCreating(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!resetEmail) {
      toast.error("Email is required");
      return;
    }
    
    setResetting(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/password-reset/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: resetEmail })
      });
      
      if (res.ok) {
        toast.success("Password reset email sent (if account exists)");
        setIsResetDialogOpen(false);
        setResetEmail("");
      } else {
        toast.error("Failed to send reset email");
      }
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setResetting(false);
    }
  };

  const loadCustomerUsage = async (customerId) => {
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/usage/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCustomerUsage(data);
      }
    } catch (error) {
      console.error("Failed to load customer usage");
    }
  };

  const viewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerUsage(null);
    loadCustomerUsage(customer.id);
  };

  const updateCustomerStatus = async (customerId, status) => {
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/customers/${customerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ subscription_status: status })
      });
      
      if (res.ok) {
        toast.success(`Customer ${status === "active" ? "activated" : "suspended"}`);
        loadData();
        if (selectedCustomer?.id === customerId) {
          setSelectedCustomer(prev => ({ ...prev, subscription_status: status }));
        }
      }
    } catch (error) {
      toast.error("Failed to update customer status");
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.industry?.toLowerCase().includes(query)
    );
  });

  const industries = [
    { value: "hvac", label: "HVAC Company" },
    { value: "insurance", label: "Insurance Agency" },
    { value: "auto_repair", label: "Auto Repair Shop" },
    { value: "real_estate", label: "Real Estate Agency" },
    { value: "dental", label: "Dental Office" },
    { value: "restaurant", label: "Restaurant" },
    { value: "legal", label: "Law Firm" },
    { value: "grocery", label: "Grocery Store" },
    { value: "custom", label: "Custom Business" }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case "suspended":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Suspended</Badge>;
      case "cancelled":
        return <Badge className="bg-slate-100 text-slate-700"><AlertTriangle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Management</h1>
          <p className="text-slate-500 mt-1">Create and manage customer accounts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsResetDialogOpen(true)} data-testid="password-reset-btn">
            <Key className="w-4 h-4 mr-2" />
            Password Reset
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-customer-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Customer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-customers-input"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {plans.map(plan => (
                  <SelectItem key={plan.id || plan.slug} value={plan.id || plan.slug}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customers List */}
      <Card>
        <CardHeader>
          <CardTitle>Customers ({filteredCustomers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No customers found
              </div>
            ) : (
              filteredCustomers.map(customer => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid={`customer-row-${customer.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">
                      {customer.name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{customer.name}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {customer.email}
                        </span>
                        {customer.industry && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {customer.industry}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Badge variant="outline">{customer.subscription_plan || "free_trial"}</Badge>
                    {getStatusBadge(customer.subscription_status)}
                    
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => viewCustomer(customer)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {customer.subscription_status === "active" ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => updateCustomerStatus(customer.id, "suspended")}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-emerald-500 hover:text-emerald-700"
                          onClick={() => updateCustomerStatus(customer.id, "active")}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Customer Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
            <DialogDescription>
              Fill in the details to create a new customer account
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Corp"
                  data-testid="new-customer-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Select
                  value={newCustomer.industry}
                  onValueChange={(value) => setNewCustomer(prev => ({ ...prev, industry: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(ind => (
                      <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@acme.com"
                data-testid="new-customer-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={newCustomer.password}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Secure password"
                data-testid="new-customer-password"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={newCustomer.phone_number}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="+1234567890"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan">Subscription Plan</Label>
                <Select
                  value={newCustomer.plan_id}
                  onValueChange={(value) => setNewCustomer(prev => ({ ...prev, plan_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.filter(p => p.is_active !== false).map(plan => (
                      <SelectItem key={plan.id || plan.slug} value={plan.id || plan.slug}>
                        {plan.name} (${plan.price_monthly}/mo)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newCustomer.notes}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes about this customer"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="safe_mode"
                checked={newCustomer.safe_mode_enabled}
                onCheckedChange={(checked) => setNewCustomer(prev => ({ ...prev, safe_mode_enabled: checked }))}
              />
              <Label htmlFor="safe_mode">Enable Safe Mode (AI restrictions)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createCustomer} disabled={creating} data-testid="submit-create-customer">
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Password Reset</DialogTitle>
            <DialogDescription>
              Enter the customer's email to send a password reset link
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="reset_email">Customer Email</Label>
              <Input
                id="reset_email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="customer@example.com"
                data-testid="reset-email-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={sendPasswordReset} disabled={resetting} data-testid="submit-password-reset">
              {resetting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Reset Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedCustomer?.name}</DialogTitle>
            <DialogDescription>{selectedCustomer?.email}</DialogDescription>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedCustomer.subscription_status)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Plan</p>
                  <p className="font-medium capitalize">{selectedCustomer.subscription_plan?.replace(/_/g, " ")}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Industry</p>
                  <p className="font-medium capitalize">{selectedCustomer.industry?.replace(/_/g, " ") || "Not set"}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium">{new Date(selectedCustomer.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              {customerUsage && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Usage</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-900">{customerUsage.usage?.minutes_used || 0}</p>
                      <p className="text-sm text-blue-600">Minutes Used</p>
                      {customerUsage.limits?.minutes > 0 && (
                        <p className="text-xs text-blue-500">of {customerUsage.limits.minutes}</p>
                      )}
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-900">{customerUsage.usage?.contacts_count || 0}</p>
                      <p className="text-sm text-purple-600">Contacts</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-900">{customerUsage.usage?.campaigns_count || 0}</p>
                      <p className="text-sm text-emerald-600">Campaigns</p>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedCustomer.notes && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-slate-600">{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
              Close
            </Button>
            {selectedCustomer?.subscription_status === "active" ? (
              <Button
                variant="destructive"
                onClick={() => updateCustomerStatus(selectedCustomer.id, "suspended")}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Suspend Customer
              </Button>
            ) : (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => updateCustomerStatus(selectedCustomer.id, "active")}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate Customer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
